import { fromJS, Map as ImmutableMap, isImmutable } from 'immutable';

import { getLogger } from '../../utils/core';
import type {
  GetPreferencesSuccessPayload,
  PreferenceDocument,
  PreferencePath,
  PreferencesStore,
  SetPreferencePayload,
} from './preferencesTypes';
import { isPreferenceDocument } from './preferencesUtils';

const log = getLogger('data/preferences/preferencesReducer.js');

const isArray = (value: unknown): value is readonly unknown[] => Array.isArray(value);

// mergeDeep concatenates arrays (https://github.com/facebook/immutable-js/issues/762), so every array
// in the update replaces the merged one, as a List.
function mergeDeepReplaceArrays(
  current: ImmutableMap<string, unknown>,
  update: PreferenceDocument,
): ImmutableMap<string, unknown> {
  let merged = current.mergeDeep(update);
  const replaceArrays = (data: object, basePath: readonly string[]) => {
    Object.entries(data).forEach(([key, rawValue]: [string, unknown]) => {
      // read Immutable values (e.g. a List of Records) as plain JS
      const value: unknown = isImmutable(rawValue) ? rawValue.toJS() : rawValue;
      const path = [...basePath, key];
      if (isArray(value)) {
        if (merged.hasIn(path)) {
          merged = merged.setIn(path, fromJS(value));
        }
      } else if (typeof value === 'object' && value !== null) {
        replaceArrays(value, path);
      }
    });
  };
  replaceArrays(update, []);
  return merged;
}

/** Merges a preference document into its group and marks the group as loaded. */
export function mergePreferences(
  state: PreferencesStore,
  { section, group }: PreferencePath,
  preferences: PreferenceDocument,
): PreferencesStore {
  log.debug('data for create/replace is ', preferences, { section, group });
  const merged = mergeDeepReplaceArrays(ImmutableMap<string, unknown>(state.preferences[section][group]), preferences);
  // QCS returns whatever clients wrote; the declared preference types describe what this app writes.
  const newState = state.mergeIn(['preferences', section, group], merged).setIn(['loadPending', section, group], false);
  log.debug('PREFS END AS ', newState.toJS());
  return newState;
}

export function mergeFetchedPreferences(
  state: PreferencesStore,
  { resources }: GetPreferencesSuccessPayload,
): PreferencesStore {
  const [response] = resources;
  if (!response?.path) {
    return state;
  }
  // An empty or non-object body still completes the load.
  return mergePreferences(state, response.path, isPreferenceDocument(response.data) ? response.data : {});
}

export function mergeSetPreference(state: PreferencesStore, payload: SetPreferencePayload): PreferencesStore {
  // Untyped callers have sent payloads without a preference; those change nothing.
  return isPreferenceDocument(payload.preference) ? mergePreferences(state, payload, payload.preference) : state;
}
